import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../landing/Header";
import Footer from "../landing/Footer";
import CookieBanner from "../landing/CookieBanner";
import Constructor from "../landing/Constructor";
import Examples from "../landing/Examples";
import RoiCalculator from "../landing/RoiCalculator";
import Faq from "../landing/Faq";
import useReveal from "../landing/useReveal";
import useSeo from "../landing/useSeo";
import { SITE, pluralRu, yearsOnMarket } from "../landing/siteConfig";
import "../landing/landing.css";

const nf = (n) => Math.round(n).toLocaleString("ru-RU");

const Counter = ({ target }) => {
    const [value, setValue] = useState(0);
    useEffect(() => {
        let start = null;
        const dur = 1100;
        let raf;
        const tick = (ts) => {
            if (!start) start = ts;
            const p = Math.min(1, (ts - start) / dur);
            setValue(target * (1 - Math.pow(1 - p, 3)));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target]);
    return <b>{nf(value)}</b>;
};

const Hero = () => {
    const [ref, visible] = useReveal();
    const years = yearsOnMarket();
    return (
        <section className={`hero${visible ? "" : ""}`} id="hero" ref={ref}>
            <div className="hero__glow" aria-hidden="true"></div>
            <div className="wrap hero__in">
                <div className="hero__content">
                    <span className="badge">На рынке юридических лидов с {SITE.since} года</span>
                    <h1 className="hero__title">
                        Продажа юридических лидов для юристов и адвокатов{" "}
                        <span>с выборкой по тематике и региону</span>
                    </h1>
                    <p className="hero__sub">
                        Клиенты для юристов, которым помощь нужна прямо сейчас. Купить
                        юридические лиды можно общим потоком по фиксированной цене или через
                        аукцион: назначаете ставку по каждой тематике внутри региона — чем
                        выше ставка, тем больше лидов достаётся вам.
                    </p>
                    <div className="hero__btns">
                        <a href="#constructor" className="btn btn--lg">
                            Собрать заявку под себя
                        </a>
                        <a href="#order" className="btn btn--lg btn--ghost">
                            Получить условия
                        </a>
                    </div>
                    <ul className="hero__stats">
                        <li>
                            <Counter target={12000} />
                            <span>заявок в месяц</span>
                        </li>
                        <li>
                            <Counter target={89} />
                            <span>регионов РФ</span>
                        </li>
                        <li>
                            <Counter target={30} />
                            <span>тематик права</span>
                        </li>
                        <li>
                            <Counter target={years} />
                            <span>{pluralRu(years, "год", "года", "лет")} на рынке</span>
                        </li>
                    </ul>
                    <p className="hero__note">
                        Объём заявок и число тематик — фактические данные нашей базы за
                        последние 12 месяцев. Работаем по всем субъектам РФ.
                    </p>
                </div>
            </div>
        </section>
    );
};

const ValueStrip = () => (
    <section className="strip">
        <div className="wrap strip__in">
            <div className="strip__item">
                <p>
                    Вам больше не нужно тратить время на сайты, контекст, SEO и соцсети,
                    чтобы получать клиентов.
                </p>
            </div>
            <div className="strip__item strip__item--bold">
                <p>Мы ищем вам клиентов — вы продаёте им свои юридические услуги.</p>
            </div>
            <div className="strip__item">
                <p>
                    Покупаете готовые заявки на консультацию и работаете с ними без лишних
                    забот.
                </p>
            </div>
        </div>
    </section>
);

const setMode = (mode) => window.dispatchEvent(new CustomEvent("lp:set-mode", { detail: mode }));

const Formats = () => {
    const [ref, visible] = useReveal();
    return (
        <section className={`sect reveal${visible ? " is-in" : ""}`} id="formats" ref={ref}>
            <div className="wrap">
                <div className="sect__head">
                    <span className="eyebrow">Два формата закупки</span>
                    <h2 className="sect__title">
                        Берите весь поток региона по фиксированной цене или выигрывайте нужные
                        тематики в аукционе
                    </h2>
                    <p className="sect__lead">
                        Вы сами решаете, как покупать: весь поток выбранного региона по
                        заранее оговорённой цене либо через аукцион — внутри кампании на
                        регион выставляете ставки по нужным направлениям права.
                    </p>
                </div>

                <div className="formats">
                    <article className="fcard">
                        <div className="fcard__top">
                            <span className="fcard__tag">Формат 1</span>
                            <h3 className="fcard__title">Общий поток</h3>
                            <p className="fcard__sub">
                                Весь поток выбранного региона по заранее оговорённой цене
                            </p>
                        </div>
                        <ul className="fcard__list">
                            <li>
                                <b>Все направления права</b> без отбора — берёте поток региона
                                целиком
                            </li>
                            <li>
                                <b>Фиксированная цена</b> за лид, оговоренная заранее — она не
                                меняется от спроса
                            </li>
                            <li>
                                <b>Максимальный объём</b> — до нескольких тысяч заявок в месяц
                            </li>
                            <li className="is-warn">
                                <b>Без отбраковки</b> — лиды не возвращаются и не заменяются
                            </li>
                            <li>Регионы выбираете сами — поток идёт по ним целиком</li>
                        </ul>
                        <p className="fcard__who">
                            <b>Кому подходит:</b> юридическим компаниям и колл-центрам с широкой
                            практикой, которые сами прозванивают и распределяют заявки.
                        </p>
                        <a href="#constructor" className="btn btn--block" onClick={() => setMode("flow")}>
                            Собрать общий поток
                        </a>
                    </article>

                    <article className="fcard fcard--accent">
                        <div className="fcard__top">
                            <span className="fcard__tag">Формат 2</span>
                            <h3 className="fcard__title">Аукцион по тематикам</h3>
                            <p className="fcard__sub">
                                Ставка по каждой тематике внутри региона: выше ставка — больше
                                лидов
                            </p>
                        </div>
                        <ul className="fcard__list">
                            <li>
                                <b>Выбираете регион</b>, а внутри кампании — нужные направления
                                права из 30
                            </li>
                            <li>
                                <b>Назначаете ставку</b> по каждой тематике — чем она выше, тем
                                чаще лид уходит именно вам
                            </li>
                            <li>
                                <b>Ставками управляете сами</b> — подняли по тематике, получили
                                больше заявок в ней
                            </li>
                            <li className="is-ok">
                                <b>Доступна отбраковка</b> — неликвид меняем по правилам
                            </li>
                            <li>Только профильные лиды — выше конверсия в договор</li>
                        </ul>
                        <p className="fcard__who">
                            <b>Кому подходит:</b> юристам и бюро с узкой специализацией —
                            банкротство, семейное, военное, автоправо и т. д.
                        </p>
                        <a href="#constructor" className="btn btn--block" onClick={() => setMode("target")}>
                            Настроить тематики и ставки
                        </a>
                    </article>
                </div>

                <div className="cmp">
                    <table className="cmp__table">
                        <thead>
                            <tr>
                                <th>Условие</th>
                                <th>Общий поток</th>
                                <th>Аукцион по тематикам</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td data-l="Условие">Выбор тематик</td>
                                <td data-l="Общий поток">
                                    <span className="no">нет, поток региона целиком</span>
                                </td>
                                <td data-l="Аукцион">
                                    <span className="yes">да, любые из 30</span>
                                </td>
                            </tr>
                            <tr>
                                <td data-l="Условие">Как формируется цена</td>
                                <td data-l="Общий поток">
                                    <span className="yes">фиксированная, оговорена заранее</span>
                                </td>
                                <td data-l="Аукцион">ваша ставка по каждой тематике</td>
                            </tr>
                            <tr>
                                <td data-l="Условие">Что даёт цена</td>
                                <td data-l="Общий поток">весь поток региона</td>
                                <td data-l="Аукцион">
                                    <span className="yes">чем выше ставка, тем больше лидов</span>
                                </td>
                            </tr>
                            <tr>
                                <td data-l="Условие">Управление ставками</td>
                                <td data-l="Общий поток">
                                    <span className="no">не требуется</span>
                                </td>
                                <td data-l="Аукцион">
                                    <span className="yes">меняете в любой момент</span>
                                </td>
                            </tr>
                            <tr>
                                <td data-l="Условие">Отбраковка неликвида</td>
                                <td data-l="Общий поток">
                                    <span className="no">не предусмотрена</span>
                                </td>
                                <td data-l="Аукцион">
                                    <span className="yes">по 4 причинам</span>
                                </td>
                            </tr>
                            <tr>
                                <td data-l="Условие">Выбор географии</td>
                                <td data-l="Общий поток">регионы целиком</td>
                                <td data-l="Аукцион">регионы целиком</td>
                            </tr>
                            <tr>
                                <td data-l="Условие">Объём в месяц</td>
                                <td data-l="Общий поток">
                                    <span className="yes">весь поток региона</span>
                                </td>
                                <td data-l="Аукцион">зависит от ставки и конкуренции</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
};

const WHAT_ICONS = [
    {
        path: "M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.4 0-8 2.5-8 5.5V22h16v-2.5c0-3-3.6-5.5-8-5.5Z",
        title: "Имя клиента",
        text: "Как обращаться при первом звонке",
    },
    {
        path: "M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.2.4 2.4.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.4 0 .8-.2 1l-2.3 2.2Z",
        title: "Телефон",
        text: "Проверенный номер, лид приходит горячим",
    },
    {
        path: "M12 2c-3.9 0-7 3.1-7 7 0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z",
        title: "Регион клиента",
        text: "Понимаете подсудность и работаете в своей географии",
    },
    {
        path: "M4 3h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H8l-5 4V4a1 1 0 0 1 1-1Zm3 5h10v2H7V8Zm0 4h7v2H7v-2Z",
        title: "Текст вопроса",
        text: "Готовитесь к разговору заранее и говорите по делу",
    },
];

const WhatInLead = () => {
    const [ref, visible] = useReveal();
    return (
        <section className={`sect sect--soft reveal${visible ? " is-in" : ""}`} id="what" ref={ref}>
            <div className="wrap">
                <div className="sect__head">
                    <span className="eyebrow">Состав лида</span>
                    <h2 className="sect__title">
                        Что содержит юридический лид, который вы покупаете
                    </h2>
                </div>
                <div className="whats">
                    {WHAT_ICONS.map((w) => (
                        <div className="what" key={w.title}>
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d={w.path} />
                            </svg>
                            <b>{w.title}</b>
                            <span>{w.text}</span>
                        </div>
                    ))}
                </div>
                <p className="what__note">
                    Плюс тематика заявки и время обращения — вы сразу видите, ваш это
                    профиль или нет.
                </p>
            </div>
        </section>
    );
};

const Sources = () => {
    const [ref, visible] = useReveal();
    return (
        <section className={`sect reveal${visible ? " is-in" : ""}`} id="sources" ref={ref}>
            <div className="wrap">
                <div className="sect__head">
                    <span className="eyebrow">Прозрачность</span>
                    <h2 className="sect__title">Где и как мы собираем юридические заявки</h2>
                    <p className="sect__lead">
                        Помимо собственных ресурсов мы работаем с вебмастерами и владельцами
                        сайтов юридической направленности. На этом рынке мы с {SITE.since}{" "}
                        года и дорожим репутацией надёжного партнёра. Покупка юридических лидов
                        у нас — это заявки из понятных источников, а не перепроданная база
                        контактов.
                    </p>
                </div>
                <div className="srcs">
                    <div className="src">
                        <b>Собственные сайты</b>
                        <span>Сеть собственных сайтов юридической тематики с органическим трафиком</span>
                    </div>
                    <div className="src">
                        <b>Горячая линия</b>
                        <span>Входящие звонки на бесплатную горячую линию — человек уже готов говорить</span>
                    </div>
                    <div className="src">
                        <b>Письменные обращения</b>
                        <span>Вопросы юристам с сайта: заявка приходит с полным текстом проблемы</span>
                    </div>
                    <div className="src">
                        <b>Сайты-партнёры</b>
                        <span>Проверенные вебмастера юридической тематики, работающие с нами годами</span>
                    </div>
                </div>
            </div>
        </section>
    );
};

const Quality = () => {
    const [ref, visible] = useReveal();
    return (
        <section className={`sect sect--soft reveal${visible ? " is-in" : ""}`} id="quality" ref={ref}>
            <div className="wrap">
                <div className="sect__head">
                    <span className="eyebrow">Гарантии</span>
                    <h2 className="sect__title">Отбраковка: что делать с неликвидом</h2>
                    <p className="sect__lead">
                        Все заявки фильтруются вручную от спама. Но если в формате аукциона
                        пришла неликвидная — отправляйте её в брак по одной из причин.
                    </p>
                </div>
                <div className="brak">
                    <div className="brak__item">
                        <b>Не тот регион</b>
                        <span>Проблема клиента находится в регионе, где вы не работаете</span>
                    </div>
                    <div className="brak__item">
                        <b>Неверный номер</b>
                        <span>Несуществующий или некорректный телефон</span>
                    </div>
                    <div className="brak__item">
                        <b>Не юридический вопрос</b>
                        <span>Обращение точно адресовано не юристу</span>
                    </div>
                    <div className="brak__item">
                        <b>Спам или тест</b>
                        <span>Рекламное сообщение либо тестовая заявка</span>
                    </div>
                </div>
                <div className="notice">
                    <p>
                        Многие партнёры не хотят тратить время на отбраковку и предпочитают
                        работать без неё — <b>в этом случае цена лида ниже</b>. Именно так
                        устроен формат «Общий поток».
                    </p>
                </div>
            </div>
        </section>
    );
};

const PROCESS_STEPS = [
    {
        n: 1,
        title: "Регистрируетесь в кабинете",
        text: "Заводите аккаунт за пару минут — выбираете тематики, регионы, объём и формат закупки.",
    },
    {
        n: 2,
        title: "Настраиваем кампанию",
        text: "Кампания заводится под выбранные направления и географию сразу после регистрации.",
    },
    {
        n: 3,
        title: "Пополняете баланс",
        text: "Работаем по договору, оплата от юрлица или ИП. Закрывающие документы предоставляем.",
    },
    {
        n: 4,
        title: "Получаете лиды",
        text: "Заявки приходят в кабинет, на почту и в Telegram сразу после поступления.",
    },
];

const Process = () => {
    const [ref, visible] = useReveal();
    return (
        <section className={`sect reveal${visible ? " is-in" : ""}`} id="process" ref={ref}>
            <div className="wrap">
                <div className="sect__head">
                    <span className="eyebrow">Как начать</span>
                    <h2 className="sect__title">Процесс работы</h2>
                </div>
                <ol className="steps">
                    {PROCESS_STEPS.map((s) => (
                        <li className="step" key={s.n}>
                            <span className="step__n">{s.n}</span>
                            <b>{s.title}</b>
                            <p>{s.text}</p>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    );
};

const OrderCta = () => {
    const [ref, visible] = useReveal();
    return (
        <section className={`sect sect--dark reveal${visible ? " is-in" : ""}`} id="order" ref={ref}>
            <div className="wrap">
                <div className="order">
                    <div className="order__info">
                        <span className="eyebrow eyebrow--light">Начать работу</span>
                        <h2 className="order__title">Войдите в личный кабинет, чтобы получать лиды</h2>
                        <p className="order__lead">
                            Зарегистрируйтесь и настройте формат, тематики и регионы прямо в
                            кабинете, либо войдите, если у вас уже есть аккаунт.
                        </p>
                        <ul className="order__list">
                            <li>Выберите формат: общий поток или аукцион</li>
                            <li>Настройте ставки по своим тематикам</li>
                            <li>Увидите реальный объём в своём регионе</li>
                            <li>Пополните баланс и начните получать заявки</li>
                        </ul>
                        <div className="order__contacts">
                            {SITE.phone && (
                                <>
                                    <a href={`tel:${SITE.phoneLink}`} className="order__phone">
                                        {SITE.phone}
                                    </a>
                                    <span>{SITE.phoneNote}</span>
                                </>
                            )}
                            <a href={`mailto:${SITE.email}`} className="order__mail">
                                {SITE.email}
                            </a>
                            <span>Отвечаем в рабочее время, обычно в течение дня</span>
                        </div>
                    </div>

                    <div className="auth-card">
                        <h3>Добро пожаловать</h3>
                        <p>Войдите в существующий аккаунт или зарегистрируйтесь за пару минут.</p>
                        <div className="auth-card__btns">
                            <Link to="/login" className="btn btn--lg btn--block">
                                Войти
                            </Link>
                            <Link to="/register" className="btn btn--lg btn--block btn--ghost">
                                Зарегистрироваться
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const FloatingCta = () => {
    const [on, setOn] = useState(false);

    useEffect(() => {
        const onScroll = () => {
            const order = document.getElementById("order");
            const y = window.scrollY;
            const atOrder = order ? order.getBoundingClientRect().top < window.innerHeight : false;
            setOn(y > 700 && !atOrder);
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <a href="#order" className={`fab${on ? " is-on" : ""}`}>
            Личный кабинет
        </a>
    );
};

const Landing = () => {
    const rootRef = useRef(null);

    useSeo({
        title: `Лиды для юристов — купить юридические заявки по тематике и региону | ${SITE.brand}`,
        description:
            "Продажа юридических лидов для юристов, адвокатов и юридических компаний. " +
            "Купить заявки для юристов с выборкой по тематике и региону: общий поток по фиксированной цене " +
            "или аукцион по тематикам со своими ставками. Все 89 регионов РФ, более 12 000 заявок в месяц.",
        path: "/",
        image: "/og-cover.png",
    });

    return (
        <div className="lp" ref={rootRef}>
            <Header />
            <Hero />
            <ValueStrip />
            <Formats />
            <WhatInLead />
            <Constructor />
            <Examples />
            <RoiCalculator />
            <Sources />
            <Quality />
            <Process />
            <Faq />
            <OrderCta />
            <Footer />
            <FloatingCta />
            <CookieBanner />
        </div>
    );
};

export default Landing;
